/** Patron visual del cesped. Solo afecta al render. */
export type PitchPattern =
  | "floodlit"
  | "neonHex"
  | "sunburst"
  | "terrazzo"
  | "synthScan"
  | "cropCircles"
  | "herringbone"
  | "splitDream"
  | "magmaVein"
  | "crystalShard"
  | "checkerMow"
  | "tide"
  | "aurora";

export type PitchSkinId =
  | "floodlit"
  | "neonHex"
  | "sunburst"
  | "terrazzo"
  | "synthScan"
  | "cropCircles"
  | "herringbone"
  | "splitDream"
  | "magmaVein"
  | "crystalShard"
  | "checkerMow"
  | "tide"
  | "aurora";

export interface PitchSkin {
  id: PitchSkinId;
  label: string;
  tagline: string;
  pattern: PitchPattern;
  /** Color base del terreno. */
  base: number;
  /** Tono alterno / sombra de franja. */
  stripeA: number;
  /** Tono iluminado / franja clara. */
  stripeB: number;
  /** Acento calido o de foco. */
  accent: number;
  /** Brillo secundario. */
  glow: number;
  /** Lineas del campo (0 = usar COLORS.LINE del tema global). */
  line: number;
  /** Manchas de desgaste. */
  wear: number;
  /** Franjas verticales (si el patron las usa). */
  stripes: number;
  /** Entorno: fondo, gradas, focos y particulas que acompanan al cesped. */
  env: PitchEnv;
}

/** Tipo de recinto que rodea al campo. */
export type PitchVenue = "open" | "indoor" | "void";

/** Como se mueven las particulas de ambiente. */
export type PitchMoteDrift = "float" | "rise" | "fall";

export interface PitchEnv {
  venue: PitchVenue;
  /** Color del cielo / fondo alto. */
  sky: number;
  /** Fondo bajo, hacia el horizonte. */
  skyLow: number;
  /** Color de los focos y conos de luz. */
  light: number;
  /** Tres tonos para las siluetas de grada (o de la pared en indoor). */
  crowd: readonly [number, number, number];
  /** Color de las particulas de ambiente. */
  mote: number;
  moteDrift: PitchMoteDrift;
}

export const DEFAULT_PITCH_SKIN: PitchSkinId = "floodlit";

export const PITCH_SKIN_IDS = [
  "floodlit",
  "neonHex",
  "sunburst",
  "terrazzo",
  "synthScan",
  "cropCircles",
  "herringbone",
  "splitDream",
  "magmaVein",
  "crystalShard",
  "checkerMow",
  "tide",
  "aurora",
] as const satisfies readonly PitchSkinId[];

export const PITCH_SKINS: Record<PitchSkinId, PitchSkin> = {
  floodlit: {
    id: "floodlit",
    label: "Focos Nocturnos",
    tagline: "Cesped clasico bajo focos dorados. El padron de Golazo.",
    pattern: "floodlit",
    base: 0x142f24,
    stripeA: 0x142f24,
    stripeB: 0x2a6848,
    accent: 0x3d9460,
    glow: 0xffe082,
    line: 0xf2fff5,
    wear: 0x0a1a14,
    stripes: 9,
    env: { venue: "open", sky: 0x0a1812, skyLow: 0x081610, light: 0xffe082, crowd: [0x1a3d28, 0x142e22, 0x0f2419], mote: 0xf2fff5, moteDrift: "float" },
  },
  neonHex: {
    id: "neonHex",
    label: "Red Neón",
    tagline: "Hexagonos cyber sobre cesped de noche electrica.",
    pattern: "neonHex",
    base: 0x120a1e,
    stripeA: 0x1a1230,
    stripeB: 0x221848,
    accent: 0x00e5cc,
    glow: 0xff3d9a,
    line: 0x7fffe8,
    wear: 0x0c0818,
    stripes: 7,
    env: { venue: "void", sky: 0x0a0618, skyLow: 0x160b2c, light: 0x00e5cc, crowd: [0x2a1148, 0x1c0c34, 0x3a1a5c], mote: 0x7fffe8, moteDrift: "float" },
  },
  sunburst: {
    id: "sunburst",
    label: "Sol Central",
    tagline: "Rayos desde el circulo central. Campo de culto.",
    pattern: "sunburst",
    base: 0x1a4028,
    stripeA: 0x164032,
    stripeB: 0x2d7548,
    accent: 0x4a9f5c,
    glow: 0xffd76e,
    line: 0xfff8e8,
    wear: 0x102818,
    stripes: 12,
    env: { venue: "open", sky: 0x14200e, skyLow: 0x1e2c12, light: 0xffd76e, crowd: [0x2a4a20, 0x1e3818, 0x162a12], mote: 0xffe9a8, moteDrift: "float" },
  },
  terrazzo: {
    id: "terrazzo",
    label: "Fútbol Sala",
    tagline: "Pista de sala con lineas reglamentarias FIFA y terrazo urbano.",
    pattern: "terrazzo",
    base: 0x3d4a42,
    stripeA: 0x354038,
    stripeB: 0x4a5c52,
    accent: 0xc9784a,
    glow: 0xe8a060,
    line: 0xf0ece4,
    wear: 0x2a322c,
    stripes: 6,
    env: { venue: "indoor", sky: 0x1a1e1c, skyLow: 0x242824, light: 0xfff4e0, crowd: [0x3a3028, 0x2c241e, 0x463a30], mote: 0xe8dcc8, moteDrift: "float" },
  },
  synthScan: {
    id: "synthScan",
    label: "Synthwave 88",
    tagline: "Scanlines horizontales y cesped de otro universo.",
    pattern: "synthScan",
    base: 0x12082a,
    stripeA: 0x180a34,
    stripeB: 0x241050,
    accent: 0xff2d95,
    glow: 0x00d4ff,
    line: 0xff71ce,
    wear: 0x0c0620,
    stripes: 16,
    env: { venue: "void", sky: 0x14082c, skyLow: 0x2c0a4e, light: 0xff2d95, crowd: [0x2e1152, 0x1e0a3a, 0x40186e], mote: 0x00d4ff, moteDrift: "float" },
  },
  cropCircles: {
    id: "cropCircles",
    label: "Señales",
    tagline: "Crop circles en el cesped. ¿Quien los hizo?",
    pattern: "cropCircles",
    base: 0x1a3828,
    stripeA: 0x163220,
    stripeB: 0x245a38,
    accent: 0x6ecf8a,
    glow: 0xc8f0a8,
    line: 0xe8ffe8,
    wear: 0x102818,
    stripes: 8,
    env: { venue: "open", sky: 0x0e2416, skyLow: 0x143020, light: 0xc8f0a8, crowd: [0x1c4028, 0x14301e, 0x0e2416], mote: 0xc8f0a8, moteDrift: "rise" },
  },
  herringbone: {
    id: "herringbone",
    label: "Espiga Real",
    tagline: "Patron herringbone como parquet de rey.",
    pattern: "herringbone",
    base: 0x2a4a30,
    stripeA: 0x244028,
    stripeB: 0x386848,
    accent: 0x5a9058,
    glow: 0x8fbc8f,
    line: 0xf0fff0,
    wear: 0x1a301c,
    stripes: 10,
    env: { venue: "open", sky: 0x122414, skyLow: 0x1a3220, light: 0xdfeedd, crowd: [0x24462c, 0x1a3622, 0x12281a], mote: 0xeaf6ea, moteDrift: "float" },
  },
  splitDream: {
    id: "splitDream",
    label: "Dos Mundos",
    tagline: "Mitad calida, mitad glacial. El campo se parte en dos.",
    pattern: "splitDream",
    base: 0x1e3830,
    stripeA: 0x3a2820,
    stripeB: 0x1a3048,
    accent: 0xff9060,
    glow: 0x60a8ff,
    line: 0xf2fff5,
    wear: 0x141820,
    stripes: 8,
    env: { venue: "open", sky: 0x2a1a14, skyLow: 0x14243c, light: 0xffd0b0, crowd: [0x3a2820, 0x1a3048, 0x243040], mote: 0xffffff, moteDrift: "float" },
  },
  magmaVein: {
    id: "magmaVein",
    label: "Magma",
    tagline: "Venas incandescentes bajo cesped volcánico.",
    pattern: "magmaVein",
    base: 0x1a0e0c,
    stripeA: 0x241410,
    stripeB: 0x321c14,
    accent: 0xff6020,
    glow: 0xffc040,
    line: 0xffe0a0,
    wear: 0x100808,
    stripes: 7,
    env: { venue: "open", sky: 0x180806, skyLow: 0x340e06, light: 0xff6020, crowd: [0x3a1610, 0x28100a, 0x4a1e10], mote: 0xffa040, moteDrift: "rise" },
  },
  crystalShard: {
    id: "crystalShard",
    label: "Cristal Polar",
    tagline: "Fragmentos de hielo sobre cesped artico.",
    pattern: "crystalShard",
    base: 0x142838,
    stripeA: 0x1a3048,
    stripeB: 0x284868,
    accent: 0x80d0ff,
    glow: 0xd0f0ff,
    line: 0xf0ffff,
    wear: 0x0c1824,
    stripes: 9,
    env: { venue: "open", sky: 0x0c1826, skyLow: 0x16304a, light: 0xd0f0ff, crowd: [0x1e3c56, 0x152c40, 0x27506e], mote: 0xe8f8ff, moteDrift: "fall" },
  },

  /*
   * El corte en TABLERO: lo que de verdad hace un jardinero de estadio grande.
   *
   * Faltaba justo el patron mas reconocible del futbol de verdad. Las skins de
   * cesped eran franjas verticales o dibujos inventados; esta es la que hace
   * que el campo se lea como un campo.
   */
  checkerMow: {
    id: "checkerMow",
    label: "Tablero",
    tagline: "Corte cruzado de estadio grande. El cesped que sale por television.",
    pattern: "checkerMow",
    base: 0x18402a,
    stripeA: 0x18402a,
    stripeB: 0x2b6a45,
    accent: 0x49a06d,
    glow: 0xdff5e4,
    line: 0xf6fff8,
    wear: 0x0d2a1c,
    stripes: 10,
    env: { venue: "open", sky: 0x0b1a12, skyLow: 0x09170f, light: 0xf2fff5, crowd: [0x1c4230, 0x143324, 0x0e261a], mote: 0xf2fff5, moteDrift: "float" },
  },

  /*
   * Fondo de mar, no de estadio. Ninguna skin usaba la mitad FRIA del circulo
   * cromatico para el cesped (el neon y el synthwave son morados), asi que
   * esta es la unica que cambia de verdad la temperatura del partido.
   */
  tide: {
    id: "tide",
    label: "Marea",
    tagline: "Fondo de mar en calma. Ondas que cruzan el campo entero.",
    pattern: "tide",
    base: 0x07293a,
    stripeA: 0x07293a,
    stripeB: 0x0d4257,
    accent: 0x2fb3c9,
    glow: 0x8ff0ff,
    line: 0xeafcff,
    wear: 0x041a26,
    stripes: 8,
    env: { venue: "void", sky: 0x03151f, skyLow: 0x062430, light: 0x8ff0ff, crowd: [0x0a3346, 0x072634, 0x0f4358], mote: 0x8ff0ff, moteDrift: "rise" },
  },

  /*
   * Cintas de aurora sobre hierba de noche cerrada. Es la unica que pone el
   * espectaculo ARRIBA: el cesped se apaga a proposito para que las cintas
   * tengan donde brillar.
   */
  aurora: {
    id: "aurora",
    label: "Aurora",
    tagline: "Cintas verdes y violetas sobre hierba de noche cerrada.",
    pattern: "aurora",
    base: 0x0a1a16,
    stripeA: 0x0a1a16,
    stripeB: 0x102a22,
    accent: 0x36e0a0,
    glow: 0xb98cff,
    line: 0xeafff4,
    wear: 0x061210,
    stripes: 7,
    env: { venue: "void", sky: 0x050d10, skyLow: 0x0a1820, light: 0x36e0a0, crowd: [0x123028, 0x0c221c, 0x1a3a4c], mote: 0xb98cff, moteDrift: "rise" },
  },
};


export function pitchSkin(id: PitchSkinId | string | undefined): PitchSkin {
  if (id && id in PITCH_SKINS) return PITCH_SKINS[id as PitchSkinId];
  return PITCH_SKINS[DEFAULT_PITCH_SKIN];
}

export function isPitchSkinId(id: string): id is PitchSkinId {
  return id in PITCH_SKINS;
}

export const PITCH_SKIN_STORAGE_KEY = "golazo.pitchSkin";

export function loadStoredPitchSkin(): PitchSkinId {
  try {
    const raw = localStorage.getItem(PITCH_SKIN_STORAGE_KEY);
    if (raw && isPitchSkinId(raw)) return raw;
  } catch {
    // ignore quota / private mode
  }
  return DEFAULT_PITCH_SKIN;
}

export function saveStoredPitchSkin(id: PitchSkinId): void {
  try {
    localStorage.setItem(PITCH_SKIN_STORAGE_KEY, id);
  } catch {
    // ignore
  }
}
